import { Box, Tooltip } from "@mui/material";
import {
  MedicationOutlined as SupplementIcon,
  DeleteOutlineOutlined as DeleteIcon,
  OpenInNewOutlined as LinkIcon,
} from "@mui/icons-material";
import { Card, EmptyState } from "../ui/primitives";
import { v2 } from "../ui/v2Tokens";
import Thumb from "../ui/Thumb";
import SupplementSearchField from "./SupplementSearchField";
import type { SupplementV2 } from "../model/nutritionV2Model";

type Props = {
  supplements: SupplementV2[];
  onChange: (supplements: SupplementV2[]) => void;
};

/**
 * The row's columns. Named once and shared by the header and every row, so the two cannot
 * drift apart — a labels row that no longer lines up with what it labels is worse than none.
 * Collapses to two columns on narrow screens, where the header is hidden and each field
 * carries its own placeholder instead.
 */
const COLUMNS = {
  xs: "1fr 1fr",
  md: "minmax(170px, 1.5fr) 110px 150px minmax(120px, 1.2fr) 34px",
};

const inputSx = {
  boxSizing: "border-box" as const,
  width: "100%",
  height: "34px",
  padding: "0 10px",
  borderRadius: v2.rSm,
  border: `1px solid ${v2.line}`,
  background: v2.surface,
  fontSize: "13px",
  fontFamily: "inherit",
  color: v2.ink,
  outline: "none",
  transition: "border-color .12s",
  "&::placeholder": { color: v2.ink4 },
  "&:focus": { borderColor: v2.brand },
};

const countLabel = (n: number) => (n === 0 ? "אין תוספים" : n === 1 ? "תוסף אחד" : `${n} תוספים`);

/**
 * Supplements sit at the MENU level, not inside a meal — a trainee takes them alongside
 * the plan rather than as part of one sitting. They carry no macros, so they never touch
 * the daily totals, and they are an unordered set: unlike meals, nothing about a
 * supplement list depends on which one comes first.
 */
const SupplementsSection = ({ supplements, onChange }: Props) => {
  const update = (uid: string, changes: Partial<SupplementV2>) =>
    onChange(supplements.map((s) => (s.uid === uid ? { ...s, ...changes } : s)));

  return (
    <Card data-testid="supplements-section">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "14px 16px",
          borderBottom: `1px solid ${v2.lineSoft}`,
        }}
      >
        <SupplementIcon sx={{ fontSize: 18, color: v2.ink2 }} />
        <Box sx={{ fontSize: "15px", fontWeight: 600, color: v2.ink }}>תוספי תזונה</Box>
        <Box sx={{ fontSize: "12.5px", color: v2.ink3 }}>{countLabel(supplements.length)}</Box>
        <Box sx={{ marginInlineStart: "auto", fontSize: "12px", color: v2.ink4 }}>
          אינם נספרים ביעדים היומיים
        </Box>
      </Box>

      {supplements.length === 0 ? (
        <EmptyState icon={SupplementIcon} text="חפש תוסף מרשימת התוספים של החברה כדי להוסיף אותו לתפריט" />
      ) : (
        <Box sx={{ padding: "6px 8px 8px" }}>
          {/* one labels row for the whole list, instead of a caption over every input in
              every row — the columns are identical, so saying it once is enough */}
          <Box
            aria-hidden
            sx={{
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: COLUMNS.md,
              gap: "8px",
              padding: "4px 8px 6px",
              fontSize: "11.5px",
              fontWeight: 500,
              color: v2.ink4,
            }}
          >
            <Box>תוסף</Box>
            <Box>מינון</Box>
            <Box>תזמון</Box>
            <Box>הערה</Box>
            <Box />
          </Box>

          {supplements.map((supplement) => (
            <Box
              key={supplement.uid}
              data-testid="supplement-row"
              sx={{
                display: "grid",
                gridTemplateColumns: COLUMNS,
                gap: "8px",
                alignItems: "center",
                padding: "7px 8px",
                borderRadius: v2.rSm,
                transition: "background .12s",
                "&:hover": { background: v2.hover },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "9px", minWidth: 0 }}>
                <Thumb src={supplement.imageUrl} size={34} icon={SupplementIcon} />
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                  <Box sx={{ fontSize: "13px", fontWeight: 500, color: v2.ink }}>{supplement.name}</Box>
                  {supplement.purchaseLink && (
                    <Tooltip title="קישור לרכישה" arrow>
                      <Box
                        component="a"
                        href={supplement.purchaseLink}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`קישור לרכישה של ${supplement.name}`}
                        sx={{ display: "inline-flex", color: v2.ink4, "&:hover": { color: v2.brand } }}
                      >
                        <LinkIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              <Box
                component="input"
                type="text"
                aria-label={`מינון עבור ${supplement.name}`}
                placeholder="5 גר׳"
                value={supplement.dosage || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  update(supplement.uid, { dosage: e.target.value })
                }
                sx={inputSx}
              />

              <Box
                component="input"
                type="text"
                aria-label={`תזמון עבור ${supplement.name}`}
                placeholder="אחרי האימון"
                value={supplement.timing || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  update(supplement.uid, { timing: e.target.value })
                }
                sx={inputSx}
              />

              <Box
                component="input"
                type="text"
                aria-label={`הערה עבור ${supplement.name}`}
                placeholder="הערה למתאמן"
                value={supplement.notes || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  update(supplement.uid, { notes: e.target.value })
                }
                sx={inputSx}
              />

              <Box
                component="button"
                type="button"
                aria-label={`מחק ${supplement.name}`}
                onClick={() => onChange(supplements.filter((s) => s.uid !== supplement.uid))}
                sx={{
                  width: { xs: "40px", md: "30px" },
                  height: { xs: "40px", md: "30px" },
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "6px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: v2.ink4,
                  justifySelf: "start",
                  "&:hover": { background: v2.overWash, color: v2.over },
                }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <SupplementSearchField onAdd={(supplement) => onChange([...supplements, supplement])} />
    </Card>
  );
};

export default SupplementsSection;
